import React from 'react';
import { Loader2 } from 'lucide-react';

type ObrosVariant = 'filled' | 'tonal' | 'outlined' | 'text' | 'elevated';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'danger' | 'ghost' | ObrosVariant;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Esquinas tipo pill (radius = altura / 2), como ObrosButton en Flutter. */
  pill?: boolean;
  /** Recolorea cualquier variante obros en tono danger, como ObrosButton(danger: true). */
  danger?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const legacyVariants = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  accent: 'btn-accent',
  danger: 'btn-danger',
  ghost: 'btn-ghost',
};

// Variantes de app/obros/lib/widgets/obros_button.dart
const obrosVariants: Record<ObrosVariant, string> = {
  filled: 'bg-primary-500 text-white shadow-sm hover:bg-primary-600 hover:shadow-md focus:ring-primary-400',
  tonal: 'bg-cream text-primary-700 hover:bg-cream2 focus:ring-primary-300',
  outlined: 'bg-transparent text-onSurface border border-outlineWarm hover:bg-surface-dim focus:ring-secondary-300',
  text: 'bg-transparent text-primary-600 hover:bg-primary-50 focus:ring-primary-300',
  elevated: 'bg-surface text-onSurface shadow-md hover:shadow-lg focus:ring-secondary-300',
};

const obrosDangerVariants: Record<ObrosVariant, string> = {
  filled: 'bg-danger-600 text-white shadow-sm hover:bg-danger-700 hover:shadow-md focus:ring-danger-400',
  tonal: 'bg-danger-50 text-danger-700 hover:bg-danger-100 focus:ring-danger-300',
  outlined: 'bg-transparent text-danger-600 border border-danger-300 hover:bg-danger-50 focus:ring-danger-300',
  text: 'bg-transparent text-danger-600 hover:bg-danger-50 focus:ring-danger-300',
  elevated: 'bg-surface text-danger-600 shadow-md hover:shadow-lg focus:ring-danger-300',
};

const sizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
  xl: 'h-14 px-6 text-base font-bold',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  pill = false,
  danger = false,
  fullWidth = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  ...props
}) => {
  const isObrosVariant = variant in obrosVariants;
  const obrosPalette = danger ? obrosDangerVariants : obrosVariants;
  const base = isObrosVariant
    ? `inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${obrosPalette[variant as ObrosVariant]} ${pill ? 'rounded-full' : 'rounded-xl'}`
    : legacyVariants[variant as keyof typeof legacyVariants];

  return (
    <button
      className={`${base} ${sizes[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
      {!isLoading && leftIcon}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
};
